// Pure helpers for the compliance-report viewer. Kept framework-free and
// side-effect-free so they are unit-testable against the real monitor payload
// shapes (see complianceMetrics.test.js).
//
// Context: the monitor stack emits free-form section.metrics. Some values are
// nested objects ({critical,high,medium,low}, scoreByHost) or id arrays
// (openVulnIds, containerVulnIds, …). The old viewer JSON.stringify'd those,
// which is unreadable for a host-mode report carrying dozens of CVEs. These
// helpers classify a metric so the template can render each kind readably.

import { severityWeight } from './complianceMaps.js';

const SEVERITY_KEYS = new Set(['critical', 'high', 'medium', 'low', 'info']);

/**
 * Classify a metric value for rendering.
 * @param {*} value
 * @returns {'empty'|'bool'|'scalar'|'array'|'object'}
 */
export function metricKind(value) {
  if (value === null || value === undefined) return 'empty';
  if (Array.isArray(value)) return value.length === 0 ? 'empty' : 'array';
  if (typeof value === 'boolean') return 'bool';
  if (typeof value === 'object') return Object.keys(value).length === 0 ? 'empty' : 'object';
  return 'scalar'; // number | string
}

/**
 * True when an object is shaped like a severity histogram
 * ({critical,high,medium,low[,info]}) — every key is a severity bucket.
 */
export function isSeverityObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  if (keys.length === 0) return false;
  return keys.every((k) => SEVERITY_KEYS.has(k));
}

/** camelCase / snake_case / kebab-case metric key → "Human readable" label. */
export function humanizeKey(key) {
  return String(key ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

/** Display a scalar metric: thousands-group numbers (it-IT), coerce the rest. */
export function formatScalar(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value.toLocaleString('it-IT');
  return String(value);
}

/**
 * Synthetic rollup finding? The emitter caps displayed findings per section and
 * appends an aggregate row (id 'open-vulns' or '<thing>-more'). These carry no
 * real per-CVE detail and must never be sorted/counted as a real vulnerability.
 */
export function isAggregateFinding(finding) {
  const id = String(finding?.id ?? '');
  return id === 'open-vulns' || /-more$/.test(id);
}

/**
 * Flatten every section.findings[] into a single list, tagging each finding
 * with its origin section. Order-preserving (section order, then finding order).
 */
export function flattenFindings(sections) {
  const out = [];
  for (const section of sections || []) {
    for (const f of section.findings || []) {
      out.push({ ...f, sectionKey: section.key, sectionTitle: section.title });
    }
  }
  return out;
}

/** Sorted, de-duplicated union of finding.affectedHosts across the findings. */
export function collectHosts(findings) {
  const set = new Set();
  for (const f of findings || []) {
    for (const h of f.affectedHosts || []) set.add(h);
  }
  return [...set].sort((a, b) => String(a).localeCompare(String(b)));
}

/**
 * Build pseudo-findings for the rows the period confirmed resolved. The backend
 * only carries the namespaced key "<sectionKey>::<id>" in summary.delta — there
 * is no severity/title — so these are lightweight rows shown only when the user
 * opts into "include resolved".
 */
export function resolvedRowsFromDelta(delta) {
  const ids = delta?.resolvedFindingIds || [];
  return ids.map((key) => {
    const [sectionKey, ...rest] = String(key).split('::');
    const id = rest.join('::');
    return {
      id: id || sectionKey,
      sectionKey: id ? sectionKey : '',
      sectionTitle: '',
      severity: null,
      title: id || String(key),
      status: 'resolved',
      __resolvedKey: String(key),
    };
  });
}

/** Sort weight for a severity (unknown severities sort last). */
export function weightOf(severity) {
  return severity in severityWeight ? severityWeight[severity] : 9;
}
